import type { ReviewExhibitContent } from "@/components/editorial/sectors/content";
import { NORTHSTAR_ANSWER } from "./answer-content";

// Northstar's evidence is the same record set the previous chapter answered from.
const northstarEvidence = NORTHSTAR_ANSWER.records.map(record => ({
  source: record.name,
  title: `${record.system.split(" / ")[0]} · ${record.title}`,
  detail: `${record.value}. ${record.detail}`,
}));

/** Fictional customer records for the home story's closing chapter. */
export const NORTHSTAR_REVIEW: ReviewExhibitContent = {
  title: "Weekly customer review",
  area: "Customer operations",
  question: "Which customers need a follow-up this week?",
  view: "Customer follow-ups",
  recordLabel: "Customer",
  dateLabel: "Next step",
  summary: [
    ["Three customers need a follow-up before Friday. The other five are on track."],
    [{ strong: "Northstar" }, "’s £8,400 invoice is overdue. The client expects to pay on Friday, and ", { strong: "Maya Chen" }, " will check for the payment. ", { strong: "Alder & Co" }, " still needs to share system access before onboarding. ", { strong: "Meridian Foods" }, " has not replied to the revised quote, and no one owns the follow-up."],
  ],
  nextStep: "Confirm Northstar’s payment on Friday and name an owner for Meridian’s quote.",
  records: [
    { name: "Northstar", status: "Invoice overdue", tone: "attention", owner: "Maya Chen", date: "Fri",
      answer: "The website is delivered and INV-2048 for £8,400 is overdue. The client expects to pay on Friday. Maya owns the follow-up and will check for the payment then.",
      evidence: [
        ...northstarEvidence,
        { source: "Follow-up", title: "Slack · Check for payment on Friday", detail: "Maya Chen will check for the payment on Friday and chase the client if it has not arrived." },
      ] },
    { name: "Alder & Co", status: "Access needed", tone: "attention", owner: "Leah James", date: "Fri",
      answer: "Alder’s onboarding starts once the client shares system access. Leah has asked for it. The start date stays provisional until access arrives.",
      evidence: [
        { source: "Project", title: "Linear · Onboarding plan", detail: "Onboarding is planned to start on Friday, once system access is available." },
        { source: "Follow-up", title: "Slack · Access requested", detail: "Leah James has asked the client for system access. No reply is recorded yet." },
      ] },
    { name: "Meridian Foods", status: "Owner to confirm", tone: "waiting", owner: "Unassigned", date: "Fri",
      answer: "Meridian has not replied to the revised quote, and no one is recorded as following up. Name an owner before the quote expires on Friday.",
      evidence: [
        { source: "Quote", title: "HubSpot · Revised quote", detail: "Revised quote sent last week. It expires on Friday." },
        { source: "Conversation", title: "Gmail · No reply recorded", detail: "No client reply to the revised quote is recorded." },
      ] },
    { name: "Orbit Education", status: "Paid", tone: "ready", owner: "Daniel Cole", date: "Next Mon",
      answer: "Orbit paid its latest invoice on time and the next milestone is on track. No follow-up is needed this week.",
      evidence: [{ source: "Invoice", title: "Xero · INV-2031", detail: "Paid in full last week." }] },
    { name: "Brightwater Energy", status: "On track", tone: "ready", owner: "Sam Patel", date: "Next Tue",
      answer: "Brightwater’s project is on track for Tuesday’s check-in. Sam owns the next update.",
      evidence: [{ source: "Project", title: "Linear · Monthly check-in", detail: "Check-in scheduled for next Tuesday. No open blockers are recorded." }] },
    { name: "Arcstone Legal", status: "Paid", tone: "ready", owner: "Daniel Cole", date: "Next Wed",
      answer: "Arcstone paid its retainer invoice this week. Wednesday’s planning call is booked, and Daniel owns the agenda.",
      evidence: [{ source: "Invoice", title: "Xero · INV-2044", detail: "Retainer invoice paid in full on Monday." }] },
    { name: "Copperline Retail", status: "On track", tone: "ready", owner: "Leah James", date: "Next Thu",
      answer: "Copperline’s rollout is on track for Thursday’s milestone. Leah owns the next client update.",
      evidence: [{ source: "Project", title: "Linear · Store rollout", detail: "Milestone due next Thursday. No open blockers are recorded." }] },
    { name: "Fernway Property", status: "Invoice sent", tone: "ready", owner: "Maya Chen", date: "Next Fri",
      answer: "Fernway’s £3,200 invoice falls due next Friday. Nothing is overdue yet.",
      evidence: [{ source: "Invoice", title: "Xero · INV-2057", detail: "£3,200 due next Friday. Not yet overdue." }] },
  ],
  boundary: "Example customer records. Your team checks the sources before chasing a client or changing a commitment.",
};
